<template>
  <v-dialog v-model="open" width="400">
    <v-card v-if="!displayAlert">
      <v-card-title>
        <template v-if="editing">{{$t('activity_reservation.edit.request_modification')}}</template>
        <template v-else-if="cancelling">{{$t(`activity_reservation.edit.cancel.${reservationMethod.type}`)}}</template>
        <template v-else>{{$t('activity_reservation.edit.title')}}</template>
      </v-card-title>
      <template v-if="editing">
        <v-card-text>
          <reservation-date-and-time-selector :people="people" :initialDate="read.requestedDate" :initialSlot="read.requestedTime" :pageId="item.reservation.pageId" :activityId="item.activity.id" :reservationMethod="reservationMethod" />
          <v-textarea filled v-model="reason" :label="$t('fields.reason')"/>
          <v-checkbox
            v-if="reservationMethod && !autoAcceptModification"
            v-model="permission"
            :label="$t('activity_reservation.edit.permission') + (!!item.order ? ` ${$t('activity_reservation.edit.order_refund_disclaimer')}*` : '*')"
            color="primary darken-1"
          >
          </v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-layout row class="ma-0 wrap-reverse" justify-end>
          <v-btn color="primary darken-1" text @click="open = false">{{ $t('actions.cancel') }}</v-btn>
          <v-btn color="primary darken-1" :loading="loading" :disabled="!validEdition" outlined @click="requestModification()">{{ $t('activity_reservation.edit.request_modification') }}</v-btn>
          </v-layout>
        </v-card-actions>
      </template>
      <template v-else-if="cancelling">
        <v-card-text>
          <v-textarea autofocus filled v-model="reason" :label="$t('fields.reason')"/>
        </v-card-text>
        <v-card-actions>
          <v-layout row class="ma-0 wrap-reverse" justify-end>
          <v-btn color="primary darken-1" text @click="open = false">{{ $t('actions.cancel') }}</v-btn>
          <v-btn color="primary darken-1" :loading="loading" outlined @click="cancelReservation()">{{ $t(`activity_reservation.edit.cancel.${reservationMethod.type}`) }}</v-btn>
          </v-layout>
        </v-card-actions>
      </template>
      <template v-else>
        <v-card-text>
          <div v-if="reservationMethod && editionAvailable && !autoAcceptModification">{{$t('activity_reservation.edit.must_be_approved')}}</div>
        </v-card-text>
        <v-card-text 
          v-if=" status === activityReservationStatus.ACCEPTED_BY_ACTIVITY_MODIFICATION ||
          status === activityReservationStatus.ACCEPTED ||
          status === activityReservationStatus.PENDING ||
          status === activityReservationStatus.PENDING_MODIFICATION"
        >
          <!--modify reservation button-->
          <v-tooltip v-if="minimumModificationTime !== null" bottom :disabled="editionAvailable"  max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
              <v-btn 
                :disabled="!editionAvailable" 
                outlined 
                block 
                color="primary" 
                @click="editing = true"
              >
                {{ $t(`activity_reservation.edit.modify.${reservationMethod.type}`) }}
              </v-btn>
              </div>
            </template>
            {{ $t(`activity_reservation.edit.unavailable_edition.${reservationMethod.type}`, { time: formatDuration({ms:minimumModificationTime}) })}}
          </v-tooltip>
          <!--cancel reservation button-->
          <v-tooltip bottom :disabled="cancellationAvailable" v-if="minimumCancellationTime !== null" max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on">
              <v-btn :class="{'mt-2': minimumModificationTime !== null}" :disabled="!cancellationAvailable" outlined block color="primary" @click="cancelling = true">{{$t(`activity_reservation.edit.cancel.${reservationMethod.type}`)}}</v-btn>
              </div>
            </template>
            {{$t(`activity_reservation.edit.unavailable_cancellation.${reservationMethod.type}`, { time: formatDuration({ms:minimumCancellationTime}) })}}
          </v-tooltip>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary darken-1" text @click="open = false">{{ $t('actions.close') }}</v-btn>
        </v-card-actions>
      </template>
      <!-- MODIFICATION ALERT -->
    </v-card>
    <v-card v-else>
      <v-card-title>
        <v-layout column align-center>
          <div class="headline text-center mb-4">{{resultInformation.title}}</div>
          <v-icon :color="modificationAlertData.color" x-large>{{ resultInformation.icon }}</v-icon>
          <div v-if="resultInformation.text" class="caption text-center mt-4">{{resultInformation.text}}</div>
        </v-layout>
      </v-card-title>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary darken-1" text @click="open = false">{{ $t('actions.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { activityReservationStatus, modificationRequestConfig } from '@/config'
import { mapState } from 'vuex'
import { colonHourToMs, formatDuration } from '@/lib/helpers/HoursFormatter'
export default {
  props: {
    triggerOpen: Date,
    item: Object,
  },
  data () {
    return {
      activityReservationStatus,
      open: false,
      editing: false,
      cancelling: false,
      permission: false,
      loading: false,
      formatDuration,
      typeError: null,
    }
  },
  computed: {
    ...mapState({
      page: (state) => state.pages.page,
      edited: (state) => state.activityReservations.edited,
      modificationAlert: state => state.activityReservations.modificationAlert,
    }),
    read () {
      return this.item ? this.item.reservation : null
    },
    reason: {
      get () {
        return this.edited.reason
      },
      set (value) {
        this.$store.commit('activityReservations/setField', {
          field: 'reason',
          value,
        })
      },
    },
    people () {
      if (!this.item) return 0
      return this.item.nominalAttendees.length + this.item.numeralAttendees.reduce((acc, { quantity }) => acc + quantity, 0)
    },
    reservationMethod () {
      if (!this.read) {
        return null
      }
      const rm = this.item.reservationMethods.find((rM) => {
        return rM.activityReservationMethod.type === this.read.type
      })
      return rm ? rm.activityReservationMethod : null
    },
    status () {
      if (!this.read) return null
      return this.read.status
    },
    editionAvailable () {
      return this.reservationMethod && this.reservationMethod.minimumModificationTime !== null
        ? moment().isBefore(moment(this.read.acceptedDate || this.read.requestedDate).add(this.read.acceptedTime || this.read.requestedTime, 'ms').subtract(this.reservationMethod.minimumModificationTime, 'ms'))
        : false
    },
    autoAcceptModification () {
      return this.reservationMethod && this.reservationMethod.autoAcceptModification
    },
    cancellationAvailable () {
      return this.reservationMethod && this.reservationMethod.minimumCancellationTime !== null
        ? moment().isBefore(moment(this.read.acceptedDate || this.read.requestedDate).add(this.read.acceptedTime || this.read.requestedTime, 'ms').subtract(this.reservationMethod.minimumCancellationTime, 'ms'))
        : false
    },
    minimumModificationTime () {
      return this.reservationMethod && this.reservationMethod.minimumModificationTime !== null ? this.reservationMethod.minimumModificationTime : null
    },
    minimumCancellationTime () {
      return this.reservationMethod && this.reservationMethod.minimumCancellationTime !== null ? this.reservationMethod.minimumCancellationTime : null
    },
    validEdition () {
      return (
        (this.edited.date ? moment(this.edited.date).format('YYYY-MM-DD') !== this.read.requestedDate : true) ||
        (this.edited.slot ? colonHourToMs(this.edited.slot) !== this.read.requestedTime : true)
      ) &&
      (this.autoAcceptModification || this.permission)
    },
    displayAlert () {
      return !!this.modificationAlertData && (this.editing || this.cancelling)
    },
    modificationAlertData () {
      return this.modificationAlert ? modificationRequestConfig[this.modificationAlert.key] : null
    },

    resultInformation () {
      let title = null
      let text = null
      let icon = this.modificationAlertData.icon

      if (this.$te(`activity_reservation.error.${ this.typeError }.refund`)) {
        title = this.$t(`activity_reservation.error.${ this.typeError }.refund`)
      } else {
        title = this.$te(`activity_reservation.edit.${ this.modificationAlert.action }_${ this.modificationAlertData.key }.${ this.reservationMethod.type }`)
          ? this.$t(`activity_reservation.edit.${ this.modificationAlert.action }_${ this.modificationAlertData.key }.${ this.reservationMethod.type }`)
          : this.$t(`activity_reservation.edit.${ this.modificationAlert.action }_${ this.modificationAlertData.key }`)
      }

      return {
        title,
        text,
        icon,
      }
    },
  },
  methods: {
    requestModification () {
      this.loading = true
      this.typeError = null
      this.$store.dispatch('activityReservations/requestModification').then(() => {
        this.$store.commit('activityReservations/setModificationAlert', { key: this.autoAcceptModification ? modificationRequestConfig.success.key : modificationRequestConfig.pending.key, action: 'modification' })
      }).catch(() => {
        this.$store.commit('activityReservations/setModificationAlert', { key: modificationRequestConfig.error.key, action: 'modification' })
      }).finally(() => {
        this.loading = false
      })
    },
    cancelReservation () {
      this.loading = true
      this.typeError = null
      this.$store.dispatch('activityReservations/cancelReservation').then(() => {
        this.$store.commit('activityReservations/setModificationAlert', { key: modificationRequestConfig.success.key, action: 'cancellation' })
        this.$store.commit('state/setMessage', { text: this.$t(`activity_reservation.edit.cancellation_success.${ this.reservationMethod.type }`), color: 'success' })
      }).catch((error) => {
        this.$store.commit('activityReservations/setModificationAlert', { key: modificationRequestConfig.error.key, action: 'cancellation' })
        this.typeError = error.body.errors[0].name
      }).finally(() => {
        this.loading = false
      })
    },
  },
  watch: {
    triggerOpen () {
      this.open = true
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$store.commit('activityReservations/reset')
          this.$store.commit('activityReservations/setField', {
            field: 'id',
            value: this.read.id,
          })
        }
      },
      immediate: true,
    },
    cancelling: {
      handler: function (cancelling) {
        if (cancelling) {
          this.$store.commit('activityReservations/reset')
          this.$store.commit('activityReservations/setField', {
            field: 'id',
            value: this.read.id,
          })
        }
      },
      immediate: true,
    },
    open (open) {
      if (!open) {
        this.editing = false
        this.cancelling = false
        this.typeError = null
        if (this.modificationAlert && [modificationRequestConfig.success.key, modificationRequestConfig.pending.key].includes(this.modificationAlert.key)) {
          this.$emit('reload')
        }
        this.$store.commit('activityReservations/setModificationAlert', null)
      }
    },
  },
  components: {
    ReservationDateAndTimeSelector: () => import('@/components/view-components/reservations/ReservationDateAndTimeSelector.vue'),
  },
}
</script>
